import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Menu, Dropdown } from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  AuditOutlined,
  UserDeleteOutlined,
  UsergroupAddOutlined,
  EditOutlined,
  FolderAddOutlined,
  FormOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

import { UploadFileModal } from 'common/components';

import Messages from 'i18n/messages/maintenance';
import * as memberMaintenanceActions from 'pages/member-maintenance/controllers/actions';

import * as api from 'config/axios';

import * as memberMaintainEnpoints from 'services/memberMaintenance/endpoints';

import RibbonButton from 'common/components/button/RibbonButton';
import ButtonSmallIcon from 'common/components/button/ButtonSmallIcon';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';
import {
  CREATE_MEMBER_PERMISSION,
  CREATE_USER_PERMISSION,
  EDIT_MEMBER_PERMISSION,
  EDIT_USER_PERMISSION,
} from 'static/Permission';
import { useCheckPermissions } from 'hooks/useCheckPermissions';

import { UPLOAD_MAX_SIZE } from 'static/Constants';

import './MemberTemplateSection.less';

const { SubMenu } = Menu;

const mapToMemberValidateGrid = (data) => {
  const nextData = _.cloneDeep(data);
  const allValidateKeys = Object.keys(nextData);
  allValidateKeys.forEach((templateKey) => {
    nextData[templateKey] = nextData[templateKey].map((rowItem) => ({
      checkbox: true,
      ...rowItem,
      uuid: uuidv4().toString(),
    }));
  });
  return nextData;
};

const MemberTemplateSection = (props) => {
  const [isUploadTemplateModalOpen, setIsUploadTemplateModalOpen] =
    useState(false);

  const dispatch = useDispatch();

  const isCreateMember = useCheckPermissions(CREATE_MEMBER_PERMISSION);

  const isCreateUser = useCheckPermissions(CREATE_USER_PERMISSION);

  const isEditMember = useCheckPermissions(EDIT_MEMBER_PERMISSION);

  const isEditUser = useCheckPermissions(EDIT_USER_PERMISSION);

  const isReadOnlyMemberOrUser =
    !isCreateMember && !isCreateUser && !isEditMember && !isEditUser;

  const downloadEditMemberTemplate = () => {
    api.sendDownload({
      url: memberMaintainEnpoints.GET_TEMPLATE_EDIT_MEMBER,
    });
  };

  const downloadCreateMemberTemplate = () => {
    api.sendDownload({
      url: memberMaintainEnpoints.GET_TEMPLATE_CREATE_MEMBER,
    });
  };

  const downloadEditUserTemplate = () => {
    api.sendDownload({
      url: memberMaintainEnpoints.GET_TEMPLATE_EDIT_USER,
    });
  };

  const downloadCreateUserTemplate = () => {
    api.sendDownload({
      url: memberMaintainEnpoints.GET_TEMPLATE_CREATE_USER,
    });
  };

  const toggleUploadTemplateModal = () => {
    setIsUploadTemplateModalOpen((prevIsOpen) => !prevIsOpen);
  };

  const handleUploadMemberOrUserTemplate = (result) => {
    if (result?.data?.isSuccess) {
      const proccessGridData = mapToMemberValidateGrid(result?.data?.data);
      toggleUploadTemplateModal();
      dispatch(
        memberMaintenanceActions.setTemplateValidation(proccessGridData)
      );
    }
  };

  const renderEditTemplateOpions = (
    <SubMenu
      title={<ButtonSmallIcon icon={<FormOutlined />} label={'Edit'} />}
      popupClassName='rb-member-communicate-section-submenu'
    >
      <Menu.Item>
        <RibbonButton
          icon={
            <div style={{ display: 'inline-block', position: 'relative' }}>
              <UsergroupAddOutlined />
              <EditOutlined className='rb-member-communicate-section-submenu__icon-subicon' />
            </div>
          }
          label={'Member'}
          size='small'
          disabled={!isEditMember}
          onClick={downloadEditMemberTemplate}
        />
      </Menu.Item>
      <Menu.Item key={'Load'}>
        <RibbonButton
          icon={
            <div style={{ display: 'inline-block', position: 'relative' }}>
              <UserDeleteOutlined />
              <EditOutlined className='rb-member-communicate-section-submenu__icon-subicon' />
            </div>
          }
          label={'User'}
          size='small'
          disabled={!isEditUser}
          onClick={downloadEditUserTemplate}
        />
      </Menu.Item>
    </SubMenu>
  );

  const renderCreateTemplateOpions = (
    <SubMenu
      title={<ButtonSmallIcon icon={<FolderAddOutlined />} label={'Create'} />}
      popupClassName='rb-member-communicate-section-submenu'
    >
      <Menu.Item>
        <RibbonButton
          icon={<UsergroupAddOutlined />}
          label={'Member'}
          size='small'
          disabled={!isCreateMember}
          onClick={downloadCreateMemberTemplate}
        />
      </Menu.Item>
      <Menu.Item key={'Load'}>
        <RibbonButton
          icon={<UserAddOutlined />}
          label={'User'}
          size='small'
          disabled={!isCreateUser}
          onClick={downloadCreateUserTemplate}
        />
      </Menu.Item>
    </SubMenu>
  );

  const menuOptions = (
    <Menu className='rb-member-communicate-section-menu'>
      <SubMenu
        key='Download'
        popupClassName='rb-member-communicate-section-submenu'
        style={{ backgroundColor: 'inherit' }}
        title={
          <RibbonButton
            icon={<DownloadOutlined />}
            label={'Download Template'}
            size='small'
          />
        }
      >
        {renderEditTemplateOpions}
        {renderCreateTemplateOpions}
      </SubMenu>

      <Menu.Item key={'Load'}>
        <RibbonButton
          icon={<UploadOutlined />}
          label={'Load Template'}
          size='small'
          onClick={toggleUploadTemplateModal}
        />

        <UploadFileModal
          apiUrl={memberMaintainEnpoints.MEMBER_MAINTAIN_VALIDATE_BULK_TEMPLATE}
          title='Upload Templates'
          isOpen={isUploadTemplateModalOpen}
          onCancel={toggleUploadTemplateModal}
          width={700}
          bodyStyle={{ height: 600 }}
          isUploadMultipleModal
          fileParamName='TemplateFiles'
          showServerMessage
          getResponse={handleUploadMemberOrUserTemplate}
          maxSize={UPLOAD_MAX_SIZE.GENERAL}
          supportTypes={['xlsx', 'xls']}
        />
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown overlay={menuOptions} disabled={isReadOnlyMemberOrUser}>
      <RibbonButton icon={<AuditOutlined />} label={Messages.template} />
    </Dropdown>
  );
};

export default MemberTemplateSection;
